import React, { useState, useRef, useEffect, useCallback } from 'react'
import type { GovernedClient } from '../../../types'
import './ClientDropdown.css'

export interface ClientDropdownProps {
  selectedClientId?: number | null
  onClientSelect: (client: GovernedClient) => void
  placeholder?: string
  disabled?: boolean
  clients?: GovernedClient[]
  loading?: boolean
  error?: Error | null
}

const ClientDropdown: React.FC<ClientDropdownProps> = ({
  selectedClientId,
  onClientSelect,
  placeholder = "Search clients...",
  disabled = false,
  clients = [],
  loading = false,
  error = null
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const selectedClient = clients.find(c => c.id === selectedClientId) ?? null

  const getClientDisplayName = (client: GovernedClient) =>
    `${client.first_name} ${client.last_name}`.trim() || client.client_identifier

  const getClientSecondaryInfo = (client: GovernedClient) => {
    const parts = []
    if (client.client_identifier) parts.push(client.client_identifier)
    if (client.company) parts.push(client.company)
    return parts.join(' • ')
  }

  // Sync input value when selected client changes externally
  useEffect(() => {
    if (selectedClient && !isSearching) {
      setInputValue(getClientDisplayName(selectedClient))
    }
  }, [selectedClient, isSearching])

  const filteredClients = isSearching && inputValue.trim()
    ? clients.filter(client => {
        const q = inputValue.toLowerCase()
        return (
          client.first_name?.toLowerCase().includes(q) ||
          client.last_name?.toLowerCase().includes(q) ||
          client.client_identifier?.toLowerCase().includes(q) ||
          client.company?.toLowerCase().includes(q)
        )
      })
    : clients

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
    setIsSearching(true)
    setIsOpen(true)
  }

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true)
    }
  }

  const handleClear = useCallback(() => {
    setInputValue('')
    setIsSearching(true)
    setIsOpen(true)
    inputRef.current?.focus()
  }, [])

  const handleClientSelect = (client: GovernedClient) => {
    onClientSelect(client)
    setInputValue(getClientDisplayName(client))
    setIsSearching(false)
    setIsOpen(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      // Restore previous selection if any
      if (selectedClient) {
        setInputValue(getClientDisplayName(selectedClient))
        setIsSearching(false)
      }
      setIsOpen(false)
      inputRef.current?.blur()
    }
  }

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        // Restore to selected client name if search was in progress
        if (isSearching && selectedClient) {
          setInputValue(getClientDisplayName(selectedClient))
          setIsSearching(false)
        } else if (isSearching && !selectedClient) {
          setInputValue('')
          setIsSearching(false)
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isSearching, selectedClient])

  if (error) {
    return (
      <div className="client-dropdown-error">
        <span className="error-text">Failed to load clients</span>
      </div>
    )
  }

  const showClear = inputValue.length > 0 && !disabled && !loading

  return (
    <div className={`client-dropdown ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      <div className={`client-dropdown-trigger combobox ${isOpen ? 'open' : ''}`}>
        {selectedClient && !isSearching && (
          <div className="selected-client-avatar combobox-avatar">
            <span className="selected-client-initials">
              {selectedClient.first_name?.[0]}{selectedClient.last_name?.[0]}
            </span>
          </div>
        )}
        <input
          ref={inputRef}
          className="client-search-input"
          type="text"
          value={loading ? 'Loading clients...' : inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || loading}
          autoComplete="off"
        />
        {showClear ? (
          <button className="client-clear-btn" onClick={handleClear} tabIndex={-1} type="button">
            <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        ) : (
          <svg
            className={`client-dropdown-arrow ${isOpen ? 'rotated' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
            onClick={() => { if (!disabled && !loading) { setIsOpen(o => !o); inputRef.current?.focus() } }}
          >
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        )}
      </div>

      {isOpen && !loading && (
        <div className="client-dropdown-menu">
          <div className="client-list">
            {filteredClients.length === 0 ? (
              <div className="no-clients-found">
                {inputValue ? `No clients found matching "${inputValue}"` : 'No clients available'}
              </div>
            ) : (
              filteredClients.map(client => (
                <div
                  key={client.id}
                  className={`client-option ${selectedClientId === client.id ? 'selected' : ''}`}
                  onMouseDown={(e) => { e.preventDefault(); handleClientSelect(client) }}
                >
                  <div className="client-option-avatar">
                    <span className="client-option-initials">
                      {client.first_name?.[0]}{client.last_name?.[0]}
                    </span>
                  </div>
                  <div className="client-option-info">
                    <span className="client-option-name">{getClientDisplayName(client)}</span>
                    <span className="client-option-secondary">{getClientSecondaryInfo(client)}</span>
                    {client.risk_profile && (
                      <span className={`risk-profile-badge risk-profile-${client.risk_profile}`}>
                        {client.risk_profile}
                      </span>
                    )}
                  </div>
                  {client.is_active && (
                    <div className="active-indicator">
                      <svg viewBox="0 0 8 8" fill="currentColor"><circle cx="4" cy="4" r="4" /></svg>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientDropdown
